/**
 * «Dove lo metto» (rule 47-ter): where the express goes in Modula 1 when it
 * does not fit in the boxes that already hold it.
 *
 * The dialog owns the state of the step and nothing else: it talks to the API
 * only through the four roads it is given (read the proposal again, check the
 * choices, save the pieces per box, load), so the bench decides how a gesture
 * is sent and what happens after it.
 *
 * - Products without pieces per box get a field first; after «Salva» the
 *   proposal is read again.
 * - A tap on a choice (green box or its row), then a tap on a free slot or an
 *   existing box, moves it (`moveAssignment`, the same move the domain knows);
 *   the server then checks the new plan and says warnings and problems.
 * - A warning needs its own «Confermo»; moving a choice drops the
 *   confirmations of that choice. Problems block.
 * - «Il cassetto è cambiato» reads the proposal again and shows it with that
 *   sentence on top: nothing was sent.
 */

import { useRef, useState } from 'react';
import { MODULA_1_DRAWERS, moveAssignment } from '@errebi/domain';
import type {
  AssegnazionePosto,
  ConfermaPosti,
  EsitoComando,
  EsitoPosti,
  PostiExpress,
} from '@errebi/shared';

import { Modale } from '../../../components/modal';
import { CapacityField } from './capacity-field';
import { DrawerDrawing, type PlaceTarget } from './drawer-drawing';

export type Proposta = Extract<EsitoPosti, { esito: 'proposta' }>;

export const DRAWERS_UNREADABLE = 'Non riesco a leggere i cassetti del Modula, riprova.';

/** The refusals of the load that leave the dialog open: the plan is still to fix. */
const STAY_OPEN = new Set(['Dove lo metto', 'Avvisi da confermare', 'Cassetti non leggibili']);
const DRAWER_CHANGED = 'Cassetto cambiato';

export interface PlacementDialogProps {
  /** Which gesture the step belongs to: it changes only the words. */
  readonly gesture: 'express' | 'close';
  readonly note: string;
  readonly initial: Proposta;
  readonly load: () => Promise<PostiExpress>;
  readonly check: (scelte: AssegnazionePosto[]) => Promise<PostiExpress>;
  readonly saveCapacity: (productId: number, pezzi: number) => Promise<EsitoComando>;
  readonly confirm: (conferma: ConfermaPosti) => Promise<EsitoComando>;
  /** The load went (or was refused for good): the dialog is over. */
  readonly onDone: (esito: EsitoComando) => void;
  readonly onClose: () => void;
}

const total = (list: readonly { pieces: number }[]): number =>
  list.reduce((sum, a) => sum + a.pieces, 0);

export function outText(n: number): string {
  return n === 1 ? '1 pezzo resta fuori' : `${n} pezzi restano fuori`;
}

const message = (e: unknown): string => (e instanceof Error ? e.message : String(e));

export function PlacementDialog({
  gesture,
  note,
  initial,
  load,
  check,
  saveCapacity,
  confirm,
  onDone,
  onClose,
}: PlacementDialogProps) {
  const [data, setData] = useState<Proposta>(initial);
  const [assignments, setAssignments] = useState<AssegnazionePosto[]>([
    ...initial.proposal.assignments,
  ]);
  const [result, setResult] = useState<Proposta['check']>(initial.check);
  const [confirmed, setConfirmed] = useState<string[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /**
   * The plan on screen is the one the server checked last. A move makes it
   * unchecked until its answer arrives; a check that fails (refusal, drawers
   * unreadable, network) leaves it unchecked, with «Riprova il controllo»: the
   * warnings on screen belong to an older plan and must never be sent.
   */
  const [checked, setChecked] = useState<'ok' | 'pending' | 'failed'>('ok');
  /** Drawers the operator asked to see besides the ones the proposal touches. */
  const [extra, setExtra] = useState<number[]>([]);
  /** Only the last check counts: an older answer must not paint over a newer move. */
  const round = useRef(0);

  /** A fresh proposal replaces everything the operator did on the old one. */
  function apply(r: PostiExpress): boolean {
    if ('ok' in r) {
      setNotice(r.motivo);
      return false;
    }
    if (r.esito === 'cassetti-illeggibili') {
      setNotice(DRAWERS_UNREADABLE);
      return false;
    }
    setData(r);
    setAssignments([...r.proposal.assignments]);
    setResult(r.check);
    setConfirmed([]);
    setSelected(null);
    setChecked('ok');
    return true;
  }

  async function reload(then: string | null) {
    round.current += 1;
    setBusy(true);
    try {
      if (apply(await load())) setNotice(then);
    } catch (e) {
      setNotice(message(e));
    } finally {
      setBusy(false);
    }
  }

  async function saveFor(productId: number, pezzi: number) {
    setBusy(true);
    try {
      const esito = await saveCapacity(productId, pezzi);
      if (!esito.ok) {
        setNotice(esito.motivo);
        setBusy(false);
        return;
      }
    } catch (e) {
      setNotice(message(e));
      setBusy(false);
      return;
    }
    await reload(null);
  }

  async function moveTo(target: PlaceTarget) {
    if (selected === null) return;
    const moved = moveAssignment(assignments, selected, target);
    // The choices that changed (the moved one, or the one it merged into) lose
    // their confirmations: what was confirmed was another box.
    const changed = new Set(
      moved
        .filter((a) => {
          const before = assignments.find((b) => b.id === a.id);
          return before === undefined || before !== a;
        })
        .map((a) => a.id),
    );
    changed.add(selected);
    const dropped = new Set(
      result.ok ? result.warnings.filter((w) => changed.has(w.assignment)).map((w) => w.id) : [],
    );
    setAssignments(moved);
    setConfirmed((c) => c.filter((id) => !dropped.has(id)));
    setSelected(null);
    await runCheck(moved);
  }

  /** The server checks this plan; only the answer to the last request counts. */
  async function runCheck(plan: AssegnazionePosto[]) {
    setNotice(null);
    setChecked('pending');
    const mine = ++round.current;
    setBusy(true);
    let failure: string | null = null;
    try {
      const r = await check(plan);
      if (mine !== round.current) return;
      if ('ok' in r) failure = r.motivo;
      else if (r.esito === 'cassetti-illeggibili') failure = DRAWERS_UNREADABLE;
      else {
        // The drawers as read now; the plan stays the operator's.
        setData(r);
        setResult(r.check);
        const live = new Set(r.check.ok ? r.check.warnings.map((w) => w.id) : []);
        setConfirmed((c) => c.filter((id) => live.has(id)));
        setChecked('ok');
      }
    } catch (e) {
      if (mine === round.current) failure = message(e);
    } finally {
      if (mine === round.current) {
        if (failure !== null) {
          setNotice(failure);
          setChecked('failed');
        }
        setBusy(false);
      }
    }
  }

  function tap(target: PlaceTarget, here: readonly string[]) {
    if (here.length === 0 || target.kind === 'box') {
      // A free slot, or an existing box (goods already there): the choice goes there.
      if (selected !== null && !here.includes(selected)) {
        void moveTo(target);
        return;
      }
      if (here.length === 0) return;
    } else if (selected !== null && !here.includes(selected)) {
      // A new box holding only other choices: tapping it picks its choice, it does not merge.
      setSelected(here[0]!);
      return;
    }
    // Tapping the box of the selected choice again picks the next one in it, or lets go.
    const at = selected === null ? -1 : here.indexOf(selected);
    setSelected(at + 1 < here.length ? here[at + 1]! : null);
  }

  async function send() {
    setBusy(true);
    let esito: EsitoComando;
    try {
      esito = await confirm({ scelte: assignments, confermati: confirmed });
    } catch (e) {
      setBusy(false);
      onDone({ ok: false, titolo: 'Errore', motivo: message(e), regola: '—' });
      return;
    }
    if (!esito.ok && esito.titolo === DRAWER_CHANGED) {
      await reload(esito.motivo);
      return;
    }
    setBusy(false);
    if (!esito.ok && STAY_OPEN.has(esito.titolo)) {
      setNotice(esito.motivo);
      return;
    }
    onDone(esito);
  }

  const { proposal, facts, capacities } = data;
  const warnings = result.ok ? result.warnings : [];
  const problems = result.ok ? [] : result.problems;
  const unconfirmed = warnings.some((w) => !confirmed.includes(w.id));
  const missing = proposal.missingCapacity;
  const out =
    total(proposal.leftovers) + Math.max(0, total(proposal.assignments) - total(assignments));
  const drawers = [
    ...new Set([...proposal.assignments, ...assignments].map((a) => a.target.drawer).concat(extra)),
  ].sort((a, b) => a - b);
  // Any Modula 1 drawer with somewhere to put goods: a free slot or a group box.
  const others = MODULA_1_DRAWERS.filter(
    (d) =>
      !drawers.includes(d) &&
      (data.slots.some((s) => s.drawer === d) ||
        data.boxes.some((b) => b.drawer === d && b.group !== null && b.container !== 'other')),
  );
  const productName = (productId: number): string =>
    Object.values(facts).find((f) => f.productId === productId)?.productName ??
    `prodotto ${productId}`;
  const describe = (a: AssegnazionePosto): string => {
    const f = facts[a.article];
    const what = f ? `${f.productName} ${f.size} ${f.color}` : a.article;
    const where = a.target.kind === 'new' ? 'scatola nuova' : 'scatola esistente';
    return `${a.pieces} × ${what} → cassetto ${a.target.drawer}, ${where}`;
  };
  const blocked =
    busy || checked !== 'ok' || missing.length > 0 || problems.length > 0 || unconfirmed;

  return (
    <Modale etichetta={`Dove lo metto · bolla ${note}`} larga ampia onChiudi={onClose}>
      <div className="posto">
        <b className="gesto__k">Dove lo metto</b>
        <p className="mod__hint">
          {gesture === 'close'
            ? `Bolla ${note}: l’express non sta tutto nelle sue scatole. Scegli il posto, poi la bolla si chiude.`
            : `Bolla ${note}: l’express non sta tutto nelle sue scatole. Scegli il posto e caricalo.`}{' '}
          Tocca una scatola verde, poi un posto libero o una scatola, per spostarla.
        </p>

        {notice !== null && (
          <p className="avviso avviso--no posto__nota" role="status">
            {notice}
          </p>
        )}

        {missing.map((id) => (
          <CapacityField
            key={id}
            productId={id}
            productName={productName(id)}
            initial={capacities[String(id)]}
            busy={busy}
            onSave={(productId, pezzi) => void saveFor(productId, pezzi)}
          />
        ))}

        {drawers.map((d) => (
          <DrawerDrawing
            key={d}
            drawer={d}
            boxes={data.boxes.filter((b) => b.drawer === d)}
            slots={data.slots.filter((s) => s.drawer === d)}
            assignments={assignments.filter((a) => a.target.drawer === d)}
            facts={facts}
            capacities={capacities}
            selected={selected}
            onTap={tap}
          />
        ))}

        {checked === 'failed' && (
          <div className="mods">
            <button className="mod" disabled={busy} onClick={() => void runCheck(assignments)}>
              Riprova il controllo
            </button>
          </div>
        )}

        {others.length > 0 && (
          <label className="posto__altro-cassetto">
            <span className="mod__hint">Mostra un altro cassetto</span>
            <select
              className="mod"
              value=""
              onChange={(e) => {
                const d = Number(e.target.value);
                if (d) setExtra((x) => (x.includes(d) ? x : [...x, d]));
              }}
            >
              <option value="">Scegli…</option>
              {others.map((d) => (
                <option key={d} value={d}>
                  Cassetto {d}
                </option>
              ))}
            </select>
          </label>
        )}

        {out > 0 && <p className="posto__fuori">{outText(out)}</p>}
        {data.unknownReasons.map((r) => (
          <p key={r} className="mod__hint">
            {r}
          </p>
        ))}

        {assignments.length > 0 && (
          <ul className="posto__scelte">
            {assignments.map((a) => (
              <li key={a.id}>
                <button
                  className="mod posto__scelta"
                  aria-pressed={selected === a.id}
                  onClick={() => setSelected(selected === a.id ? null : a.id)}
                >
                  {describe(a)}
                </button>
              </li>
            ))}
          </ul>
        )}

        {problems.map((p) => (
          <p key={p} className="avviso avviso--no posto__nota">
            {p}
          </p>
        ))}
        {warnings.map((w) => (
          <p key={w.id} className="avviso avviso--no posto__nota posto__avviso">
            <span>{w.text}</span>
            {confirmed.includes(w.id) ? (
              <span className="mod__hint">Confermato</span>
            ) : (
              <button
                className="mod"
                onClick={() => setConfirmed((c) => (c.includes(w.id) ? c : [...c, w.id]))}
              >
                Confermo
              </button>
            )}
          </p>
        ))}

        <div className="mods" style={{ marginTop: 12 }}>
          <button className="mod" onClick={onClose}>
            Non caricare <span className="mod__hint">Esc</span>
          </button>
          <button className="mod mod--si" disabled={blocked} onClick={() => void send()}>
            Carica nel Modula
          </button>
        </div>
      </div>
    </Modale>
  );
}
